import { useStaticQuery, graphql } from "gatsby"
import React, { useState, useEffect, useMemo } from "react"
import CategoryFilter from "components/CategoryFilter"
import FixedSubscriptionPopup from "components/FixedSubscriptionPopup"
import { SEOHead } from "components/SEO"
import { SIGNUP_URL } from "constants/urls"
import { useBlogCategories } from "effects/useSiteContent"
import NewsletterSection from "components/Blocks/NewsletterSection"
import Section from "components/Layout/Section"
import SimpleHeader from "components/Layout/SimpleHeader"
import * as css from "components/pages/blog/Blog.module.css"
import BlogPostListingItem from "components/pages/blog/BlogPostListingItem"
import wavesBackground from "components/pages/blog/waves-bg.svg"
import Layout from "components/ui/Layout"

function BlogPage({ location: { pathname } }) {
  const [activeCategory, setActiveCategory] = useState(null)

  useEffect(() => {
    function handleScroll() {
      const popupDom = document.getElementById("subscription-popup")
      if (popupDom) {
        const currentScrollPos = window?.pageYOffset
        if (currentScrollPos > 500)
          popupDom.style.transform = "translate(-50%, 0%)"
        else popupDom.style.transform = "translate(-50%, 150%)"
      }
    }
    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  /** Blog categories from local YAML */
  const blogCategories = useBlogCategories()

  const data = useStaticQuery(graphql`
    {
      allMarkdownRemark(
        filter: { fields: { collection: { eq: "blog" } } }
        sort: { frontmatter: { date: DESC } }
      ) {
        nodes {
          fields {
            slug
          }
          frontmatter {
            title
            author
            category
            date
            thumbnail
            thumbnailAlt
            externalUrl
          }
          thumbnailImage {
            childImageSharp {
              gatsbyImageData(quality: 100, layout: FULL_WIDTH)
            }
          }
        }
      }
    }
  `)

  // Transform local blogs to listing format
  const allPosts = useMemo(
    () =>
      data.allMarkdownRemark.nodes.map((blog) => ({
        slug: blog.frontmatter.externalUrl || blog.fields?.slug,
        title: blog.frontmatter.title,
        author: blog.frontmatter.author,
        category: blog.frontmatter.category,
        date: blog.frontmatter.date,
        thumbnailAlt: blog.frontmatter.thumbnailAlt,
        thumbnail: blog.thumbnailImage?.childImageSharp
          ? {
              gatsbyImageData:
                blog.thumbnailImage.childImageSharp.gatsbyImageData,
            }
          : blog.frontmatter.thumbnail?.startsWith("http")
            ? { url: blog.frontmatter.thumbnail }
            : null,
      })),
    [data]
  )

  const today = new Date()
  const isPreview = process.env.GATSBY_IS_PREVIEW === "true"
  const posts = allPosts.filter(
    (post) =>
      // Not listing out blog posts in the future
      // allowing for URLs to be referenced ahead of announcements
      // Preview deployments show all posts including future-dated ones
      isPreview || post.date <= today.toISOString().split("T")[0]
  )

  return (
    <Layout
      headerFloating
      trackingName="Blog"
      pageContentType="listing-page"
      pageName="blog"
      pagePath={pathname}
    >
      <Section className={css.hero}>
        <SimpleHeader
          level="h1"
          label="From the Preset Community"
          title="The Preset Blog"
          titleSize="xxl"
          dash="horizontal"
        />
      </Section>
      <Section className={css.blogListingSection}>
        <CategoryFilter
          categories={blogCategories}
          onSelectItem={setActiveCategory}
        />
        <div className={css.gridContainer}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, index) => {
              if (!activeCategory || post.category === activeCategory) {
                return (
                  <div key={post.slug || index} className={css.column}>
                    <BlogPostListingItem
                      slug={post.slug}
                      title={post.title}
                      author={post.author}
                      category={post.category}
                      thumbnail={post.thumbnail}
                      thumbnailAlt={post.thumbnailAlt}
                    />
                  </div>
                )
              }
              return null
            })}
          </div>
        </div>
      </Section>
      <NewsletterSection
        heading="Let's get visual. Try Preset today."
        darkTheme
        ctaButton={{
          buttonLabel: "Start for free",
          trackingName: "Start for free (Footer)_Blog",
          url: SIGNUP_URL,
          color: "white",
          roundedCorner: true,
        }}
      />
      <FixedSubscriptionPopup />
      <div className={css.bgContainer}>
        <img
          className={css.bgImage}
          src={wavesBackground}
          alt="Waves background"
        />
      </div>
    </Layout>
  )
}

// Export a Head component for Gatsby Head API
export function Head() {
  return <SEOHead title="Blog" />
}

export default BlogPage
